import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Columns3,
  CopyPlus,
  EllipsisVertical,
  Eye,
  FileEdit,
  Link2,
  MoveRight,
  Plus,
  RotateCcw,
  Search,
  SendHorizonal,
  Trash,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ActionGateBanner } from '@/components/patterns/ActionGateBanner';
import { BulkActionBar } from '@/components/patterns/BulkActionBar';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import { authoringStatusMeta, STATUS_OPTIONS, StatusPill } from '@/components/patterns/StatusPill';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCopyWorkflowLink } from '@/features/sessions/api/useCopyWorkflowLink';
import { NewSessionDrawer } from '@/features/sessions/components/NewSessionDrawer';
import { useEmailVerified } from '@/hooks/useEmailVerified';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { useApiCatalogue } from '../api/useApiCatalogue';
import { useBulkDeleteWorkflows } from '../api/useBulkDeleteWorkflows';
import { useBulkWorkflowStatus, type WorkflowStatusTarget } from '../api/useBulkWorkflowStatus';
import { useDeleteWorkflow } from '../api/useDeleteWorkflow';
import { useDuplicateWorkflow } from '../api/useDuplicateWorkflow';
import { useListWorkflows } from '../api/useListWorkflows';
import { usePermanentDeleteWorkflow } from '../api/usePermanentDeleteWorkflow';
import { usePublishWorkflow } from '../api/usePublishWorkflow';
import { useRenameWorkflow } from '../api/useRenameWorkflow';
import { useRestoreWorkflow } from '../api/useRestoreWorkflow';
import { useUnpublishWorkflow } from '../api/useUnpublishWorkflow';
import { CreateWorkflowModal } from '../components/CreateWorkflowModal';
import type { WorkflowDto } from '../schemas/workflowSchemas';
import { StepIcon, stepLabel } from '../stepIcons';
import { STEP_TYPE_CATALOGUE_TAGS, STEP_TYPE_META } from '../types';

// The editor route for a workflow (list-based "simple" vs node "graph").
const editHref = (w: WorkflowDto) =>
  w.structure === 'Simple' ? `/workflows/${w.id}/simple` : `/workflows/${w.id}/editor`;

// Action-menu row styling — matches the designer's dropdown (muted icon + label).
const ACTION_ITEM =
  'flex items-center gap-3 px-2 py-2 text-[13px] font-medium text-gray30 cursor-pointer hover:text-primary';
const ACTION_ICON = 'w-4 h-4 text-gray66';

// View / Publish / Unpublish / Restore / Delete are intentionally HIDDEN from the
// action menu (the design shows Edit · Copy link · New Session · Rename · Duplicate
// · Archive; status transitions live on the inline status picker). The code is kept
// and re-enabled by flipping this flag — nothing was removed.
const LEGACY_ACTIONS: boolean = false;

const PAGE_SIZES = [10, 15, 20, 30, 50];

function shortId(id: string) {
  // Use the TAIL of the UUID, not the head. Workflow ids are time-ordered
  // (UUIDv7), so every row created around the same time shares the same
  // leading chars (all showed "#019F"). The last 6 chars vary per row and
  // stay stable per workflow. There is no numeric/sequential id from the API.
  return `#${id.replace(/-/g, '').slice(-6).toUpperCase()}`;
}

// Total estimated cost per completed session — SAME formula as the editor's
// own price line (SimpleWorkflowEditorScreen.tsx): the real catalogue price
// for that step's tag (max across APIs sharing the tag) when one exists,
// falling back to STEP_TYPE_META's placeholder baseCost only when it doesn't.
// Using STEP_TYPE_META alone here (as before) made this column disagree with
// the editor for any step that has a real priced catalogue entry.
function workflowCost(stepTypes: string[], priceByTag: Map<string, number>): number {
  return stepTypes.reduce((sum, t) => {
    const tag = STEP_TYPE_CATALOGUE_TAGS[t];
    const realPrice = tag ? priceByTag.get(tag) : undefined;
    return sum + (realPrice ?? STEP_TYPE_META[t]?.baseCost ?? 0);
  }, 0);
}

/**
 * How many step icons fit on one line in the Features column.
 *
 * Six, the SAME number the sessions list and the questionnaires list use, so all three tables
 * truncate identically. The column is capped at 280px and each icon plus its arrow is ~31px, which is
 * where six lands.
 */
const VISIBLE_FEATURES = 6;

// The designer's per-API artwork (see stepIcons.tsx), chained with arrows.
// Each icon names its method on hover.
function FeatureIcons({ stepTypes }: { stepTypes: string[] }) {
  if (stepTypes.length === 0) return <span className="text-gray66">—</span>;

  const shown = stepTypes.slice(0, VISIBLE_FEATURES);
  const hidden = stepTypes.slice(VISIBLE_FEATURES);

  return (
    // ONE LINE, never wrapping. This was `flex-wrap`, so a ten-step workflow spilled onto a second
    // row and made that row visibly taller than its neighbours — the table lost its rhythm, and the
    // extra icons said nothing the "+N more" does not.
    <div className="flex max-w-[280px] items-center gap-1.5 whitespace-nowrap">
      {/* Keyed by INDEX, not by stepType: a workflow CAN now hold several steps of one type — a
          Questionnaire step is repeatable — so stepType alone is no longer unique, and React would
          drop every duplicate after the first. The old key was safe when it was written and stopped
          being safe when Questionnaire became repeatable.

          `stepTypes` is an ordered list of plain strings with no ids, so the index genuinely IS the
          identity here (position N in the workflow's order). The usual objection to index keys —
          state getting re-attached to the wrong item after a reorder — cannot apply: these are
          stateless icons, and the whole row re-renders from the DTO anyway. */}
      {shown.map((stepType, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: no id exists on stepTypes; see above.
        <span key={idx} className="flex shrink-0 items-center gap-1.5">
          <StepIcon stepType={stepType} />
          {idx < shown.length - 1 && <MoveRight className="w-3.5 h-3.5 shrink-0 text-gray66" />}
        </span>
      ))}

      {hidden.length > 0 && (
        <span
          className="shrink-0 text-[length:var(--text-12)] font-medium text-[#6B7280]"
          // Names what is hidden, so the badge is informative rather than only a count.
          title={hidden.map((stepType) => stepLabel(stepType)).join(', ')}
        >
          +{hidden.length} more
        </span>
      )}
    </div>
  );
}

// Two-line date+time, in the VIEWER'S local timezone (Date parses the UTC ISO
// string from the API and toLocale* converts to whatever timezone the browser
// is in) — matches the design's "28 Apr 2026" / "06:16:20 pm" stacked format,
// used for both Created and Last Updated (no relative "2 days ago" wording).
function formatDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }),
  };
}

// The three statuses a workflow can be moved between from the inline picker.
// Status vocabulary and the pill itself now come from components/patterns/StatusPill — the same
// component the questionnaires list uses. This file used to own both, and when questionnaires needed
// a status pill it grew a second implementation with its own SVG icon set; two pills for one concept
// is what `components/patterns/` exists to prevent.
export function WorkflowsListScreen() {
  const navigate = useNavigate();
  const createMatch = useMatch('/workflows/new');
  const createOpen = !!createMatch;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [workflowToPublish, setWorkflowToPublish] = useState<WorkflowDto | null>(null);
  const [workflowToUnpublish, setWorkflowToUnpublish] = useState<WorkflowDto | null>(null);
  // Delete = permanent hard delete (row removed). Archive is now reached only
  // via the Status column's inline picker (changeStatus) — no separate confirm.
  const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowDto | null>(null);
  // "New Session" preselects this workflow in the Start-Session modal.
  const [sessionWorkflow, setSessionWorkflow] = useState<{
    id: string;
    name: string;
    publicSlug: string;
  } | null>(null);
  // Rename dialog — direct, inline name edit.
  const [workflowToRename, setWorkflowToRename] = useState<WorkflowDto | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Single-row status change from the inline picker — awaiting confirmation
  // (matches the design's "Are you sure?" popup; not a direct no-confirm change).
  const [statusChangeRequest, setStatusChangeRequest] = useState<{
    workflow: WorkflowDto;
    target: (typeof STATUS_OPTIONS)[number];
  } | null>(null);

  // Multi-select for bulk status change (bottom action bar).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Bulk status change awaiting confirmation (null = no dialog open).
  const [bulkTarget, setBulkTarget] = useState<WorkflowStatusTarget | null>(null);
  // Bulk permanent delete awaiting confirmation.
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const bulkStatus = useBulkWorkflowStatus();
  const bulkDelete = useBulkDeleteWorkflows();
  const bulkBusy = bulkStatus.isPending || bulkDelete.isPending;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const isEmailVerified = useEmailVerified();

  const { data, isLoading } = useListWorkflows({
    page,
    pageSize,
    search: debouncedSearch || undefined,
  });
  // Full catalogue (no tag filter), fetched once and cached 5 min — used only
  // to price rows the same way the editor does (see workflowCost).
  const { data: catalogue } = useApiCatalogue();
  const priceByTag = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of catalogue ?? []) {
      if (entry.costPerCall != null && entry.isAvailable) {
        map.set(entry.tag, Math.max(map.get(entry.tag) ?? 0, entry.costPerCall));
      }
    }
    return map;
  }, [catalogue]);
  const archiveWorkflow = useDeleteWorkflow();
  const publishWorkflow = usePublishWorkflow();
  const unpublishWorkflow = useUnpublishWorkflow();
  const restoreWorkflow = useRestoreWorkflow();
  const permanentDeleteWorkflow = usePermanentDeleteWorkflow();
  const renameWorkflow = useRenameWorkflow();
  const duplicateWorkflow = useDuplicateWorkflow();
  const copyLink = useCopyWorkflowLink();

  const workflows = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  // Bulk selection (bottom action bar). Cleared when the visible page/search
  // changes so we never act on rows that are no longer shown.
  const selectedCount = selectedIds.size;
  const allOnPageSelected = workflows.length > 0 && workflows.every((w) => selectedIds.has(w.id));

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset on page/search change only
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, debouncedSearch]);

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const w of workflows) next.delete(w.id);
      } else {
        for (const w of workflows) next.add(w.id);
      }
      return next;
    });
  }

  // Runs only after the user confirms in the dialog.
  function confirmBulkStatus() {
    if (!bulkTarget) return;
    const target = bulkTarget;
    const items = workflows
      .filter((w) => selectedIds.has(w.id))
      .map((w) => ({ id: w.id, status: w.status }));
    if (items.length === 0) {
      setBulkTarget(null);
      return;
    }
    bulkStatus.mutate(
      { items, target },
      {
        onSuccess: (r) => {
          if (r.failed === 0) {
            toast.success(
              `${r.succeeded} workflow${r.succeeded === 1 ? '' : 's'} set to ${target}`,
            );
          } else {
            toast.error(`${r.succeeded} updated, ${r.failed} failed`);
          }
          setSelectedIds(new Set());
          setBulkTarget(null);
        },
        onError: () => {
          toast.error('Could not update the selected workflows');
          setBulkTarget(null);
        },
      },
    );
  }

  // Runs only after the user confirms in the dialog. Permanent — no undo.
  function confirmBulkDelete() {
    const ids = workflows.filter((w) => selectedIds.has(w.id)).map((w) => w.id);
    if (ids.length === 0) {
      setBulkDeleteOpen(false);
      return;
    }
    bulkDelete.mutate(ids, {
      onSuccess: (r) => {
        if (r.failed === 0) {
          toast.success(`${r.succeeded} workflow${r.succeeded === 1 ? '' : 's'} deleted`);
        } else {
          toast.error(`${r.succeeded} deleted, ${r.failed} failed`);
        }
        setSelectedIds(new Set());
        setBulkDeleteOpen(false);
      },
      onError: () => {
        toast.error('Could not delete the selected workflows');
        setBulkDeleteOpen(false);
      },
    });
  }

  // Delete = permanent hard delete (row removed, cannot be undone). Works
  // directly from any status now that the backend guard is removed.
  function confirmDelete() {
    if (!workflowToDelete) return;
    permanentDeleteWorkflow.mutate(workflowToDelete.id, {
      onSuccess: () => {
        toast.success('Workflow deleted');
        setWorkflowToDelete(null);
      },
      onError: () => {
        toast.error('Failed to delete workflow');
        setWorkflowToDelete(null);
      },
    });
  }

  // Inline status picker — opens the confirm popup instead of changing
  // immediately; the actual mutation runs from confirmStatusChange below.
  function requestStatusChange(wf: WorkflowDto, target: (typeof STATUS_OPTIONS)[number]) {
    if (wf.status === target) return;
    setStatusChangeRequest({ workflow: wf, target });
  }

  function confirmStatusChange() {
    if (!statusChangeRequest) return;
    changeStatus(statusChangeRequest.workflow, statusChangeRequest.target);
    setStatusChangeRequest(null);
  }

  // Runs the actual status mutation, mapping the chosen target to the right
  // endpoint(s). There is no direct Archived→Active endpoint, so that path
  // chains restore (→Draft) then publish.
  function changeStatus(wf: WorkflowDto, target: (typeof STATUS_OPTIONS)[number]) {
    if (wf.status === target) return;
    const ok = (msg: string) => () => toast.success(msg);
    const fail = (msg: string) => () => toast.error(msg);

    if (target === 'Active') {
      if (wf.status === 'Draft') {
        publishWorkflow.mutate(wf.id, {
          onSuccess: ok('Workflow activated'),
          onError: fail('Failed to activate workflow'),
        });
      } else {
        // Archived → restore then publish
        restoreWorkflow.mutate(wf.id, {
          onSuccess: () =>
            publishWorkflow.mutate(wf.id, {
              onSuccess: ok('Workflow activated'),
              onError: fail('Restored to draft, but failed to activate'),
            }),
          onError: fail('Failed to reactivate workflow'),
        });
      }
    } else if (target === 'Draft') {
      if (wf.status === 'Active') {
        unpublishWorkflow.mutate(wf.id, {
          onSuccess: ok('Workflow moved back to draft'),
          onError: fail('Failed to unpublish workflow'),
        });
      } else {
        // Archived → restore (→Draft)
        restoreWorkflow.mutate(wf.id, {
          onSuccess: ok('Workflow restored to draft'),
          onError: fail('Failed to restore workflow'),
        });
      }
    } else {
      // target Archived — soft archive from Active or Draft
      archiveWorkflow.mutate(wf.id, {
        onSuccess: ok('Workflow archived'),
        onError: fail('Failed to archive workflow'),
      });
    }
  }

  function confirmRestore(workflow: WorkflowDto) {
    restoreWorkflow.mutate(workflow.id, {
      onSuccess: () => toast.success('Workflow restored to draft'),
      onError: () => toast.error('Failed to restore workflow'),
    });
  }

  function renderPageNumbers() {
    const pages: number[] = [];
    const delta = 1;
    const start = Math.max(1, page - delta);
    const end = Math.min(totalPages, page + delta);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  function confirmPublish() {
    if (!workflowToPublish) return;
    publishWorkflow.mutate(workflowToPublish.id, {
      onSuccess: () => {
        toast.success('Workflow published');
        setWorkflowToPublish(null);
      },
      onError: () => {
        toast.error('Failed to publish workflow');
        setWorkflowToPublish(null);
      },
    });
  }

  function confirmUnpublish() {
    if (!workflowToUnpublish) return;
    unpublishWorkflow.mutate(workflowToUnpublish.id, {
      onSuccess: () => {
        toast.success('Workflow moved back to draft');
        setWorkflowToUnpublish(null);
      },
      onError: () => {
        toast.error('Failed to unpublish workflow');
        setWorkflowToUnpublish(null);
      },
    });
  }

  // Copy link — copies the workflow's shared public URL (/wf/{slug}). One common
  // link; each person who opens it gets their own session (see useCopyWorkflowLink).
  function handleCopyLink(wf: WorkflowDto) {
    copyLink.copy(wf.publicSlug);
  }

  function openRename(wf: WorkflowDto) {
    setWorkflowToRename(wf);
    setRenameValue(wf.name);
  }

  function confirmRename() {
    if (!workflowToRename) return;
    const name = renameValue.trim();
    if (!name || name === workflowToRename.name) {
      setWorkflowToRename(null);
      return;
    }
    renameWorkflow.mutate(
      { workflowId: workflowToRename.id, name },
      {
        onSuccess: () => {
          toast.success('Workflow renamed');
          setWorkflowToRename(null);
        },
        onError: () => toast.error('Failed to rename workflow'),
      },
    );
  }

  function handleDuplicate(wf: WorkflowDto) {
    duplicateWorkflow.mutate(
      { workflowId: wf.id, name: `${wf.name} (Copy)` },
      {
        onSuccess: () => toast.success('Workflow duplicated'),
        onError: () => toast.error('Failed to duplicate workflow'),
      },
    );
  }

  const anyMutationPending =
    archiveWorkflow.isPending ||
    publishWorkflow.isPending ||
    unpublishWorkflow.isPending ||
    restoreWorkflow.isPending ||
    permanentDeleteWorkflow.isPending ||
    renameWorkflow.isPending ||
    duplicateWorkflow.isPending;

  return (
    // ONE header row — heading, description, search and the action button together, exactly as the
    // questionnaires list now does. Both pages used to print their heading twice: once in the Topbar
    // from `AppShell title`, and again as a second heading here above the description.
    <AppShell
      title="Workflow"
      subtitle="Get a comprehensive view of your recent KYC checks and verification results."
      actions={
        <div className="flex items-center gap-2.5 max-sm:flex-wrap sm:justify-end">
          <div className="relative w-full xl:max-w-[330px]">
            <Search className="absolute start-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray66" />
            <input
              type="search"
              className="sc-input input-small w-full ps-9"
              placeholder="Search workflows"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/workflows/new')}
            disabled={!isEmailVerified}
            className="btn btn-primary btn-small max-[480px]:w-full disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            <span>New Workflow</span>
          </button>
        </div>
      }
    >
      {/* Full-height column so the PAGINATION SITS AT THE BOTTOM of the viewport, matching the
          sessions list. It used to sit directly under the last row, so on a short list — four
          workflows — it floated in the middle of the page with empty space beneath it. The table
          takes the free space and scrolls inside itself; `mt-auto` on the pagination pins it down. */}
      <div className="flex w-full flex-col gap-4 text-black-logo lg:h-full lg:gap-5 lg:overflow-hidden">
        {!isEmailVerified && <ActionGateBanner />}

        {/* Table region — takes the slack, scrolls internally. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* overflow-auto, not overflow-y-hidden: the wrapper is now height-bounded by the flex
              parent, so vertical clipping would cut rows off instead of letting the page grow. */}
          <div className="table-wrapper hide-scrollbar min-h-0 flex-1 overflow-auto rounded-lg border border-placeholder">
            <table className="min-w-full border-collapse text-sm leading-[17px]">
              <thead className="bg-[#F4F6FD] text-black-logo border-b border-[#CBD5E1]">
                <tr>
                  <th className="px-3 py-[8.25px] w-9 text-center">
                    <input
                      type="checkbox"
                      aria-label="Select all workflows on this page"
                      className="h-4 w-4 cursor-pointer accent-primary align-middle"
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">Id</th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start min-w-45">
                    Workflow
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Features
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Price
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Created
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-start">
                    Last Updated
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span>Status</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </div>
                  </th>
                  <th className="px-3 py-[8.25px] font-medium whitespace-nowrap text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#CBD5E1] bg-white font-normal text-gray66">
                {isLoading ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-gray66">
                      Loading…
                    </td>
                  </tr>
                ) : workflows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-gray66">
                      {debouncedSearch ? 'No workflows match your search.' : 'No workflows yet.'}
                    </td>
                  </tr>
                ) : (
                  workflows.map((workflow) => (
                    <tr
                      key={workflow.id}
                      // Clicking anywhere on the row opens the editor, matching the
                      // design — except on the row's own controls, which keep their
                      // behaviour. Two things get skipped, and BOTH are required:
                      //
                      //  1. Anything rendered outside this row's DOM. The status and
                      //     action menus are Radix dropdowns, which portal their
                      //     content to <body>; React still bubbles those clicks up
                      //     the REACT tree, so they arrive here even though the menu
                      //     is not a DOM descendant. A containment check drops them,
                      //     and covers every future portalled popover for free.
                      //  2. Interactive elements inside the row: the checkbox and
                      //     the icon buttons. (The name is a real <Link>, which also
                      //     gives keyboard users a way in — a row click cannot.)
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (!e.currentTarget.contains(target)) return;
                        if (target.closest('a, button, input, select, [role="menuitem"]')) return;
                        navigate(editHref(workflow));
                      }}
                      className={cn(
                        'cursor-pointer transition',
                        selectedIds.has(workflow.id) ? 'bg-primary/5' : 'hover:bg-placeholder/5',
                      )}
                    >
                      <td className="px-3 py-[11px] text-center">
                        <input
                          type="checkbox"
                          aria-label={`Select ${workflow.name}`}
                          className="h-4 w-4 cursor-pointer accent-primary align-middle"
                          checked={selectedIds.has(workflow.id)}
                          onChange={() => toggleOne(workflow.id)}
                        />
                      </td>
                      <td className="px-3 py-[11px] text-gray66">{shortId(workflow.id)}</td>
                      <td className="px-3 py-[11px] font-medium text-black-logo">
                        <Link
                          to={editHref(workflow)}
                          className="hover:text-primary hover:underline"
                        >
                          {workflow.name}
                        </Link>
                      </td>
                      <td className="px-3 py-[11px]">
                        <FeatureIcons stepTypes={workflow.stepTypes} />
                      </td>
                      <td className="px-3 py-[11px] font-medium text-black-logo">
                        ₹{workflowCost(workflow.stepTypes, priceByTag).toFixed(2)}
                      </td>
                      <td className="px-3 py-[11px] leading-snug">
                        <div>{formatDateTime(workflow.createdAt).date}</div>
                        <div className="text-[length:var(--text-12)] text-placeholder">
                          {formatDateTime(workflow.createdAt).time}
                        </div>
                      </td>
                      <td className="px-3 py-[11px] leading-snug">
                        <div>{formatDateTime(workflow.updatedAt).date}</div>
                        <div className="text-[length:var(--text-12)] text-placeholder">
                          {formatDateTime(workflow.updatedAt).time}
                        </div>
                      </td>
                      <td className="px-3 py-[11px] text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            asChild
                            disabled={anyMutationPending || !isEmailVerified}
                          >
                            <button
                              type="button"
                              title="Change status"
                              className="mx-auto inline-flex cursor-pointer transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <StatusPill status={workflow.status} showChevron />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="min-w-32 p-1.5">
                            {STATUS_OPTIONS.map((s) => {
                              const meta = authoringStatusMeta(s);
                              const selected = workflow.status === s;
                              return (
                                <DropdownMenuItem
                                  key={s}
                                  onClick={() => requestStatusChange(workflow, s)}
                                  className={cn(
                                    'flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-[length:var(--text-12)] font-medium',
                                    selected && meta ? meta.selected : 'text-gray30',
                                  )}
                                >
                                  <span className={cn('h-1.5 w-1.5 rounded-full', meta?.dot)} />
                                  {meta?.label ?? s}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      <td className="px-3 py-[11px]">
                        <div className="flex items-center justify-center gap-3">
                          {/* Copy link + New Session — direct icons, OUTSIDE the 3-dot
                              menu, ALWAYS visible on every row (not hidden by status).
                              Both work on a Draft, so a workflow can be TESTED before
                              publishing — the backend applies the same rule. Only
                              Archived is refused (it's retired; restore it first).
                              Disabled states keep a tooltip instead of disappearing. */}
                          <button
                            type="button"
                            title={
                              workflow.status === 'Archived'
                                ? 'Restore this workflow to copy its link'
                                : workflow.status === 'Draft'
                                  ? 'Copy link (draft — for testing)'
                                  : 'Copy link'
                            }
                            disabled={workflow.status === 'Archived'}
                            onClick={() => handleCopyLink(workflow)}
                            className="w-4 h-4 inline-flex items-center justify-center text-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Link2 className="w-full h-full" />
                          </button>
                          <button
                            type="button"
                            title={
                              workflow.status === 'Archived'
                                ? 'Restore this workflow to start a session'
                                : !isEmailVerified
                                  ? 'Verify your email to start a session'
                                  : workflow.status === 'Draft'
                                    ? 'New Session (draft — for testing)'
                                    : 'New Session'
                            }
                            disabled={workflow.status === 'Archived' || !isEmailVerified}
                            onClick={() =>
                              setSessionWorkflow({
                                id: workflow.id,
                                name: workflow.name,
                                publicSlug: workflow.publicSlug,
                              })
                            }
                            className="w-4 h-4 inline-flex items-center justify-center text-gray66 hover:text-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray66"
                          >
                            <Plus className="w-full h-full" />
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="w-4 h-4 inline-flex items-center justify-center hover:text-primary cursor-pointer"
                              >
                                <EllipsisVertical className="w-full h-full" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-36 p-1.5">
                              {/* Exactly 3 options here by design: Edit, Rename, Duplicate.
                                  Copy link / New Session live as direct icons above; status
                                  transitions (incl. Archive) live on the Status column's
                                  own picker — keeping this menu from duplicating either. */}
                              <DropdownMenuItem asChild className={ACTION_ITEM}>
                                <Link to={editHref(workflow)}>
                                  <Columns3 className={ACTION_ICON} />
                                  <span>Edit</span>
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openRename(workflow)}
                                disabled={anyMutationPending || !isEmailVerified}
                                className={ACTION_ITEM}
                              >
                                <FileEdit className={ACTION_ICON} />
                                <span>Rename</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDuplicate(workflow)}
                                disabled={anyMutationPending || !isEmailVerified}
                                className={ACTION_ITEM}
                              >
                                <CopyPlus className={ACTION_ICON} />
                                <span>Duplicate</span>
                              </DropdownMenuItem>

                              {/* Hidden but preserved — flip LEGACY_ACTIONS to restore
                                  View / Publish / Unpublish / Restore / Delete / Archive. */}
                              {LEGACY_ACTIONS && (
                                <>
                                  <DropdownMenuItem asChild className={ACTION_ITEM}>
                                    <Link to={`${editHref(workflow)}?mode=view`}>
                                      <Eye className={ACTION_ICON} />
                                      <span>View</span>
                                    </Link>
                                  </DropdownMenuItem>
                                  {workflow.status === 'Draft' && (
                                    <DropdownMenuItem
                                      onClick={() => setWorkflowToPublish(workflow)}
                                      className={ACTION_ITEM}
                                    >
                                      <SendHorizonal className={ACTION_ICON} />
                                      <span>Publish</span>
                                    </DropdownMenuItem>
                                  )}
                                  {workflow.status === 'Active' && (
                                    <DropdownMenuItem
                                      onClick={() => setWorkflowToUnpublish(workflow)}
                                      className={ACTION_ITEM}
                                    >
                                      <SendHorizonal className={cn(ACTION_ICON, 'rotate-180')} />
                                      <span>Unpublish</span>
                                    </DropdownMenuItem>
                                  )}
                                  {workflow.status === 'Archived' && (
                                    <DropdownMenuItem
                                      onClick={() => confirmRestore(workflow)}
                                      className={ACTION_ITEM}
                                    >
                                      <RotateCcw className={ACTION_ICON} />
                                      <span>Restore</span>
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => setWorkflowToDelete(workflow)}
                                    className={cn(ACTION_ITEM, 'text-red-500 hover:text-red-600')}
                                  >
                                    <Trash className={ACTION_ICON} />
                                    <span>Delete</span>
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination — pinned to the bottom by `mt-auto`, outside the scrolling table region so it
            stays visible however far the list is scrolled. */}
        {totalCount > 0 && (
          <div className="mt-auto flex shrink-0 items-center justify-between gap-4 gap-y-3 text-sm leading-[17px] text-black-light max-sm:flex-wrap">
            <p>
              Showing {rangeStart} – {rangeEnd} of {totalCount} Workflow
              {totalCount === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2 text-black max-sm:justify-center max-sm:w-full max-sm:order-last">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="min-h-5.5 min-w-5 border border-grayEF rounded-sm py-px px-1 disabled:bg-grayEF disabled:text-gray66"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {renderPageNumbers().map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'min-h-5.5 min-w-5 border border-grayEF rounded-sm py-px px-1',
                    p === page && 'border-primary text-primary',
                  )}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="min-h-5.5 min-w-5 border border-grayEF rounded-sm py-px px-1 disabled:bg-grayEF disabled:text-gray66"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                className="sc-select w-auto border-[#CCDAFF] focus:border-primary !py-[3px] ps-2 pe-6 bg-[right_6px_center] rounded-sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk action bar — now the shared pattern, so the workflows and questionnaires lists cannot
          drift. The markup lived here first; lifting it was the point. */}
      <BulkActionBar
        selectedCount={selectedCount}
        noun="workflow"
        busy={bulkBusy}
        onSetStatus={setBulkTarget}
        onDelete={() => setBulkDeleteOpen(true)}
        onClear={() => setSelectedIds(new Set())}
      />

      {/* Bulk status-change confirmation — same designer alert popup as the
          single-row picker, with the count and target status bolded. */}
      <ConfirmDialog
        open={!!bulkTarget}
        variant="alert"
        title="Are you sure?"
        description={
          bulkTarget ? (
            <>
              Are you sure you want to change the status of{' '}
              <span className="font-semibold text-black-logo">
                {selectedCount} workflow{selectedCount === 1 ? '' : 's'}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-black-logo">
                "{authoringStatusMeta(bulkTarget).label}"
              </span>
              ?
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Confirm"
        pendingLabel="Updating…"
        isPending={bulkStatus.isPending}
        onConfirm={confirmBulkStatus}
        onCancel={() => setBulkTarget(null)}
      />

      {/* Bulk delete confirmation — same alert popup. Permanent hard delete, so
          the extra "cannot be undone" line and a destructive Confirm button. */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        variant="alert"
        title="Are you sure?"
        description={
          <>
            Are you sure you want to permanently delete{' '}
            <span className="font-semibold text-black-logo">
              {selectedCount} workflow{selectedCount === 1 ? '' : 's'}
            </span>
            ? This action cannot be undone.
          </>
        }
        confirmLabel="Confirm"
        destructive
        isPending={bulkDelete.isPending}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {/* Publish confirmation */}
      <ConfirmDialog
        open={!!workflowToPublish}
        title={`Publish "${workflowToPublish?.name}"?`}
        description="This will make the workflow active and available for use. You can archive it later if needed."
        confirmLabel="Publish"
        isPending={publishWorkflow.isPending}
        onConfirm={confirmPublish}
        onCancel={() => setWorkflowToPublish(null)}
      />

      {/* Unpublish confirmation */}
      <ConfirmDialog
        open={!!workflowToUnpublish}
        title={`Unpublish "${workflowToUnpublish?.name}"?`}
        description="This will move the workflow back to draft. It will no longer be available for use until published again."
        confirmLabel="Unpublish"
        isPending={unpublishWorkflow.isPending}
        onConfirm={confirmUnpublish}
        onCancel={() => setWorkflowToUnpublish(null)}
      />

      {/* Single-row status change confirmation (inline picker) — the designer's
          alert popup: amber badge, "Are you sure?", name + target status bolded. */}
      <ConfirmDialog
        open={!!statusChangeRequest}
        variant="alert"
        title="Are you sure?"
        description={
          statusChangeRequest ? (
            <>
              Are you sure you want to change the status of{' '}
              <span className="font-semibold text-black-logo">
                "{statusChangeRequest.workflow.name}"
              </span>{' '}
              to{' '}
              <span className="font-semibold text-black-logo">
                "{authoringStatusMeta(statusChangeRequest.target).label}"
              </span>
              ?
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Confirm"
        pendingLabel="Updating…"
        isPending={anyMutationPending}
        onConfirm={confirmStatusChange}
        onCancel={() => setStatusChangeRequest(null)}
      />

      {/* Delete confirmation (permanent hard delete — cannot be undone) */}
      <ConfirmDialog
        open={!!workflowToDelete}
        title={`Delete "${workflowToDelete?.name}"?`}
        description="This permanently deletes the workflow. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        isPending={permanentDeleteWorkflow.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setWorkflowToDelete(null)}
      />

      <CreateWorkflowModal open={createOpen} onClose={() => navigate('/workflows')} />

      {/* New Session — reuses the Sessions Start-Session modal, workflow preselected */}
      <NewSessionDrawer
        open={sessionWorkflow !== null}
        workflowId={sessionWorkflow?.id ?? ''}
        workflowName={sessionWorkflow?.name ?? ''}
        publicSlug={sessionWorkflow?.publicSlug ?? ''}
        onClose={() => setSessionWorkflow(null)}
      />

      {/* Rename — direct inline name edit */}
      <RenameDialog
        open={!!workflowToRename}
        value={renameValue}
        isPending={renameWorkflow.isPending}
        onChange={setRenameValue}
        onConfirm={confirmRename}
        onCancel={() => setWorkflowToRename(null)}
      />
    </AppShell>
  );
}

// Small inline dialog for renaming a workflow (no dedicated design component —
// styled to match Studio's modals: overlay + centered card + Cancel/Save).
function RenameDialog({
  open,
  value,
  isPending,
  onChange,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  value: string;
  isPending: boolean;
  onChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-5 shadow-elevation-2">
        <h2 className="mb-1 text-lg font-semibold text-black-logo">Rename workflow</h2>
        <p className="mb-4 text-[length:var(--text-14)] text-gray66">
          Give this workflow a new name.
        </p>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirm();
            if (e.key === 'Escape') onCancel();
          }}
          placeholder="Workflow name"
          className="sc-input w-full"
        />
        <div className="mt-5 flex justify-end gap-2.5">
          <button type="button" onClick={onCancel} className="btn btn-outline btn-small">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending || !value.trim()}
            className="btn btn-primary btn-small disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
